import {
  addDays,
  areIntervalsOverlapping,
  differenceInCalendarDays,
  startOfDay,
} from 'date-fns';
import { useMemo } from 'react';
import { getScheduleEventCompositeKey } from '~/helpers/scheduleEventKey';
import { FilteredPlannerViewData, ZoomLevel } from '~/routes/calendar._index';
import { CalendarEventOrganiser } from '../CalendarEventOrganiser';
import { compareCalendarEvents } from './compareCalendarEvents';

// Utility functions for consistent cell key generation
export const createCellKey = (siteId: string, date: Date | string): string => {
  const dateString =
    date instanceof Date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(date.getDate()).padStart(2, '0')}`
      : date;
  return `${siteId}:${dateString}`;
};

export const createDraftJobKey = (
  siteId: string,
  date: Date | string
): string => {
  return `draft.job.${createCellKey(siteId, date)}`;
};

export type CalendarEvent = {
  y: number;
  x: number;
  event: FilteredPlannerViewData[number]['calendar'][number];
  eventIndex: number;
  siteId: string;
  compositeKey?: string;
};

interface UsePlannerEventsOptions {
  data: FilteredPlannerViewData;
  start: Date;
  zoomLevel: ZoomLevel;
  virtualizerRange: { startIndex: number; endIndex: number } | null;
}

/**
 * Custom hook that processes calendar events for the planner.
 * Pre-sorts and indexes events for efficient rendering.
 */
export function usePlannerEvents({
  data,
  start,
  zoomLevel,
  virtualizerRange,
}: UsePlannerEventsOptions) {
  // Pre-sort events per site ONCE when data changes - sorting is expensive
  const sortedCalendarBySite = useMemo(() => {
    return data.map((site) => {
      const sortedCalendar = [...site.calendar].sort(compareCalendarEvents);
      return { ...site, calendar: sortedCalendar };
    });
  }, [data]);

  // Calculate all events once (for height calculations)
  const allEvents = useMemo(() => {
    return data.flatMap((site, siteIndex): CalendarEvent[] => {
      return site.calendar
        .filter((event) => event.status !== 'Booking')
        .map((event, eventIndex) => ({
          y: siteIndex,
          x: differenceInCalendarDays(new Date(event.start), start),
          event,
          eventIndex,
          siteId: site.id,
          compositeKey: getScheduleEventCompositeKey(event, site.id),
        }));
    });
  }, [data, start]);

  // Filter events by visible range for rendering
  const events = useMemo(() => {
    const { startIndex, endIndex } = virtualizerRange ?? {
      startIndex: 0,
      endIndex: 0,
    };

    const roughVisibleRange = {
      start: addDays(start, startIndex - 7),
      end: addDays(start, endIndex + 10),
    };

    return sortedCalendarBySite.flatMap((site, siteIndex): CalendarEvent[] => {
      return site.calendar
        .filter((event) => {
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end || event.start);
          return areIntervalsOverlapping(
            { start: eventStart, end: eventEnd },
            roughVisibleRange
          );
        })
        .map((event, eventIndex) => ({
          y: siteIndex,
          x: differenceInCalendarDays(new Date(event.start), start),
          event,
          eventIndex,
          siteId: site.id,
          compositeKey: getScheduleEventCompositeKey(event, site.id),
        }));
    });
  }, [sortedCalendarBySite, start, virtualizerRange]);

  // Pre-compute events by cell using Map for O(1) lookup
  const eventsByCell = useMemo(() => {
    const cellMap = new Map<string, CalendarEvent[]>();

    events.forEach((calendarEvent) => {
      const { y: siteIndex, event } = calendarEvent;
      const site = data[siteIndex];
      if (!site) return;

      const eventDate = startOfDay(new Date(event.start));
      const eventEndDate = event.end
        ? startOfDay(new Date(event.end))
        : eventDate;

      let currentDate = new Date(eventDate);
      while (currentDate <= eventEndDate) {
        const cellKey = createCellKey(site.id, currentDate);
        if (!cellMap.has(cellKey)) {
          cellMap.set(cellKey, []);
        }
        cellMap.get(cellKey)!.push(calendarEvent);
        currentDate = addDays(currentDate, 1);
      }
    });

    return cellMap;
  }, [events, data]);

  // Pre-compute events indexed by site for O(1) lookup during render
  const eventsBySite = useMemo(() => {
    const siteMap = new Map<number, CalendarEvent[]>();

    events.forEach((calendarEvent) => {
      const siteIndex = calendarEvent.y;
      if (!siteMap.has(siteIndex)) {
        siteMap.set(siteIndex, []);
      }
      siteMap.get(siteIndex)!.push(calendarEvent);
    });

    return siteMap;
  }, [events]);

  // Map of siteId -> hasMultiDayJobs
  const siteMultiDayJobsMap = useMemo(() => {
    const map = new Map<string, boolean>();
    data.forEach((site) => {
      const hasMultiDayJobs = site.calendar.some((event) => {
        if (event.status === 'Booking') return false;
        const days = CalendarEventOrganiser.getCalendarEventLength(event);
        return days > 1;
      });
      map.set(site.id, hasMultiDayJobs);
    });
    return map;
  }, [data]);

  // Pre-compute job counts per site/column
  const siteJobCountsMap = useMemo(() => {
    const map = new Map<string, number[]>();
    const eventsBySiteIndex = new Map<number, CalendarEvent[]>();

    allEvents.forEach((calendarEvent) => {
      const existing = eventsBySiteIndex.get(calendarEvent.y);
      if (existing) {
        existing.push(calendarEvent);
        return;
      }
      eventsBySiteIndex.set(calendarEvent.y, [calendarEvent]);
    });

    data.forEach((site, siteIndex) => {
      const jobCounts: number[] = [];
      const siteEvents = eventsBySiteIndex.get(siteIndex) || [];

      siteEvents.forEach(({ x: columnIndex, event }) => {
        const days = CalendarEventOrganiser.getCalendarEventLength(event);
        for (let dayOffset = 0; dayOffset < days; dayOffset++) {
          const index = columnIndex + dayOffset;
          jobCounts[index] = (jobCounts[index] || 0) + 1;
        }
      });

      map.set(site.id, jobCounts);
    });

    return map;
  }, [data, allEvents]);

  // Calculate event vpos synchronously
  const { eventVpos, recentOccupied } = useMemo(() => {
    const organiser = new CalendarEventOrganiser({ events, zoomLevel });
    return {
      eventVpos: organiser.getEventVpos(),
      recentOccupied: organiser.getOccupied(),
    };
  }, [events, zoomLevel]);

  return {
    allEvents,
    events,
    eventsByCell,
    eventsBySite,
    siteMultiDayJobsMap,
    siteJobCountsMap,
    eventVpos,
    recentOccupied,
  };
}
